import React from "react";
import {
  Button,
  Card,
  Col,
  Drawer,
  Empty,
  Form,
  Input,
  Row,
  Select,
  Space,
  Table,
  Typography,
  Descriptions,
  Grid,
  Timeline,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import SolicitacaoReservaStatusTag, {
  type StatusSolicitacaoReservaFront,
} from "./components/SolicitacaoReservaStatusTag";
import ModalHospedesSolicitacao from "./components/ModalHospedesSolicitacao";
import type {
  BuscarSolicitacaoReservaByIdResponse,
  HospedeSolicitacaoReserva,
  SolicitacaoReservaListItem,
  StatusSolicitacaoReserva,
  AndamentoSolicitacao,
} from "../../services/solicitacaoReservaService";
import { formatDate as formatCivilDate } from "../../uteis/dateUtils";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

type Props = {
  loading: boolean;
  loadingDetalhe: boolean;
  data: SolicitacaoReservaListItem[];
  selected?: BuscarSolicitacaoReservaByIdResponse | null;
  drawerOpen: boolean;

  filtros: {
     contato?: string;
  empreendimento?: string;
  status?: StatusSolicitacaoReserva;
  numeroProtocolo?: string;
  };

  onChangeFiltros: (next: {
  contato?: string;
  empreendimento?: string;
  status?: StatusSolicitacaoReserva;
  numeroProtocolo?: string;
}) => void;

  onPesquisar: () => void;
  onLimpar: () => void;
  onOpenDetalhe: (item: SolicitacaoReservaListItem) => void;
  onCloseDrawer: () => void;
  novoStatus: StatusSolicitacaoReserva | null;
  onChangeNovoStatus: (status: StatusSolicitacaoReserva | null) => void;
  onAlterarStatus: (id: number, status: StatusSolicitacaoReserva) => void;
  hospedes: HospedeSolicitacaoReserva[];
  hospedesModalOpen: boolean;
  loadingHospedes: boolean;
  onVerHospedes: (token?: string | null) => void;
  onCloseHospedesModal: () => void;
  andamentos: AndamentoSolicitacao[];
  andamentosDrawerOpen: boolean;
  loadingAndamentos: boolean;
  onVerAndamentos: (id: number) => void;
  onCloseAndamentosDrawer: () => void;
  loadingEstornoTaxa?: boolean;
  onEstornarTaxa?: () => void;
  onTestarEndpoint?: (id: number) => void;
};

const statusOptions: { label: string; value: StatusSolicitacaoReserva }[] = [
  { label: "Criada", value: "CRIADA" },
  { label: "SR enviada", value: "SR_ENVIADA" },
  { label: "Iniciada", value: "INICIADA" },
  { label: "Aguardando taxa", value: "AGUARDANDO_PAGAMENTO_TAXA" },
  {
    label: "Pagamento de Taxa Interrompido",
    value: "PAGAMENTO_TX_INTERROMPIDO",
  },
  { label: "Taxa paga", value: "TAXA_PAGA" },
  { label: "Aguardando estorno", value: "AGUARDANDO_ESTORNO" },
  { label: "Estornado", value: "ESTORNADO" },
  {
    label: "Sem interesse no periodo procurado",
    value: "SEM_INTERESSE_PERIODO_PROCURADO",
  },
  {
    label: "Sem interesse no periodo encontrado",
    value: "SEM_INTERESSE_PERIODO_ENCONTRADO",
  },
  { label: "Em verificação", value: "EM_VERIFICACAO" },
  { label: "Reserva confirmada", value: "RESERVA_CONFIRMADA" },
  { label: "Reserva finalizada", value: "RESERVA_FINALIZADA" },
  { label: "Reserva cancelada", value: "RESERVA_CANCELADA" },
];

const statusColumnOptions: { label: string; value: string }[] = [
  ...statusOptions,
  { label: "Finalizado fracasso", value: "FINALIZADO_FRACASSO" },
];

const formatDateTime = (value?: string | null | Date) => {
  if (!value) {
    console.warn("⚠️ Data ausente:", value);
    return "—";
  }
  
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      console.warn("⚠️ Data inválida recebida:", value);
      return "—";
    }
    const formatted = d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    console.log("✅ Data formatada:", { original: value, formatted });
    return formatted;
  } catch (error) {
    console.error("❌ Erro ao formatar data:", error, value);
    return "—";
  }
};

const formatMoney = (value?: number | null) => {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

export default function ViewGerenciarSolicitacoesReserva({
  loading,
  loadingDetalhe,
  data,
  selected,
  drawerOpen,
  filtros,
  onChangeFiltros,
  onPesquisar,
  onLimpar,
  onOpenDetalhe,
  onCloseDrawer,
  novoStatus,
  onChangeNovoStatus,
  onAlterarStatus,
  hospedes,
  hospedesModalOpen,
  loadingHospedes,
  onVerHospedes,
  onCloseHospedesModal,
  andamentos,
  andamentosDrawerOpen,
  loadingAndamentos,
  onVerAndamentos,
  onCloseAndamentosDrawer,
  loadingEstornoTaxa,
  onEstornarTaxa,
  onTestarEndpoint,
}: Props) {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const filteredData = data.filter((item) => {
  const contatoBusca = (filtros.contato || "").toLowerCase().trim();
  const empreendimentoBusca = (filtros.empreendimento || "").toLowerCase().trim();
  const protocoloBusca = (filtros.numeroProtocolo || "").toLowerCase().trim();

  const nomeCompleto =
    `${item.contatoNome ?? ""} ${item.contatoSobrenome ?? ""}`.toLowerCase();
  const celular = (item.contatoCelular ?? "").toLowerCase();
  const email = (item.contatoEmail ?? "").toLowerCase();
  const nomeEmpreendimento = (item.empreendimento?.nome ?? "").toLowerCase();
  const numeroProtocolo = (item.numeroProtocolo ?? "").toLowerCase();

  const matchContato =
    !contatoBusca ||
    nomeCompleto.includes(contatoBusca) ||
    celular.includes(contatoBusca) ||
    email.includes(contatoBusca);

  const matchEmpreendimento =
    !empreendimentoBusca ||
    nomeEmpreendimento.includes(empreendimentoBusca);

  const matchProtocolo =
    !protocoloBusca ||
    numeroProtocolo.includes(protocoloBusca);

  const matchStatus =
    !filtros.status || item.status === filtros.status;

  return matchContato && matchEmpreendimento && matchProtocolo && matchStatus;
});

  const columns: ColumnsType<SolicitacaoReservaListItem> = [
      {
      title: "Protocolo",
      dataIndex: "numeroProtocolo",
      key: "numeroProtocolo",
      width: 160,
      render: (value) => value ?? "—",
    },
    {
      title: "Status",
      key: "status",
      width: 190,
      filters: statusColumnOptions.map((option) => ({
        text: option.label,
        value: option.value,
      })),
      onFilter: (value, record) => String(record.status) === String(value),
      render: (_, record) => (
        <SolicitacaoReservaStatusTag
          status={record.status as StatusSolicitacaoReservaFront}
        />
      ),
    },
    {
      title: "Contato",
      key: "contato",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>
            {`${record.contatoNome ?? ""} ${record.contatoSobrenome ?? ""}`.trim() || "—"}
          </div>
          <Text type="secondary">{record.contatoCelular || "—"}</Text>
          <br />
          <Text type="secondary">{record.contatoEmail || "—"}</Text>
        </div>
      ),
    },
    {
      title: "Empreendimento",
      key: "empreendimento",
      render: (_, record) => record.empreendimento?.nome ?? "—",
    },
    {
  title: "Check-in",
  key: "checkin",
  render: (_, record) =>
    record.periodoProcura?.checkin
      ? formatCivilDate(record.periodoProcura.checkin)
      : "—",
},
{
  title: "Check-out",
  key: "checkout",
  render: (_, record) =>
    record.periodoProcura?.checkout
      ? formatCivilDate(record.periodoProcura.checkout)
      : "—",
},
    {
      title: "Pagamento",
      key: "formaPagamentoPreferida",
      width: 130,
      render: (_, record) =>
        record.formaPagamentoPreferida === "CARTAO"
          ? "Cartão"
          : record.formaPagamentoPreferida === "PIX"
          ? "PIX"
          : "—",
    },
    {
      title: "Validade",
      dataIndex: "validadeProposta",
      key: "validadeProposta",
      width: 170,
      render: (value) => formatDateTime(value),
    },
    {
      title: "Ações",
      key: "acoes",
      width: 120,
      render: (_, record) => (
        <Button onClick={() => onOpenDetalhe(record)}>Detalhes</Button>
      ),
    },
  ];

  return (
    <>
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Card
          bordered={false}
          style={{ borderRadius: 16 }}
          styles={{ body: { padding: 20 } }}
        >
          <Space direction="vertical" size={4}>
            <Title level={3} style={{ margin: 0 }}>
              Gerenciar solicitações de reserva
            </Title>
            <Text type="secondary">
              Filtre, consulte e acompanhe o andamento das solicitações.
            </Text>
          </Space>
        </Card>

        <Card
          bordered={false}
          style={{ borderRadius: 16 }}
          styles={{ body: { padding: 20 } }}
        >

          <Col xs={24} md={7}>
            <Form.Item label="Protocolo">
              <Input
                value={filtros.numeroProtocolo ?? ""}
                onChange={(e) =>
                  onChangeFiltros({
                    ...filtros,
                    numeroProtocolo: e.target.value || undefined,
                  })
                }
                placeholder="Ex.: SR-2026-00015"
              />
            </Form.Item>
          </Col>
          <Form layout="vertical">
            <Row gutter={12}>
              <Col xs={24} md={7}>
          <Form.Item label="Contato">
            <Input
              value={filtros.contato ?? ""}
              onChange={(e) =>
                onChangeFiltros({
                  ...filtros,
                  contato: e.target.value || undefined,
                })
              }
              placeholder="Nome, celular ou e-mail"
            />
          </Form.Item>
</Col>

<Col xs={24} md={7}>
  <Form.Item label="Empreendimento">
    <Input
      value={filtros.empreendimento ?? ""}
      onChange={(e) =>
        onChangeFiltros({
          ...filtros,
          empreendimento: e.target.value || undefined,
        })
      }
      placeholder="Nome do empreendimento"
    />
  </Form.Item>
</Col>

              <Col xs={24} md={6}>
                <Form.Item label="Status">
                 <Select
                  allowClear
                  placeholder="Todos os status"
                  value={filtros.status ?? undefined}
                  options={statusOptions}
                  onChange={(value) =>
                    onChangeFiltros({
                      ...filtros,
                      status: value || undefined,
                    })
                  }
                />
                </Form.Item>
              </Col>

              {/* <Col xs={24} md={4}>
                <Form.Item label=" ">
                  <Space style={{ width: "100%" }}>
                    <Button type="primary" onClick={onPesquisar}>
                      Pesquisar
                    </Button>
                    <Button onClick={onLimpar}>Limpar</Button>
                  </Space>
                </Form.Item>
              </Col> */}
            </Row>
          </Form>
        </Card>

        <Card
          bordered={false}
          style={{ borderRadius: 16 }}
          styles={{ body: { padding: 0 } }}
        >
          {isMobile ? (
            <div style={{ padding: 16 }}>
              {loading ? (
                <div style={{ padding: 24, textAlign: "center" }}>Carregando...</div>
              ) : !filteredData.length ? (
                <Empty description="Nenhuma solicitação encontrada" />
              ) : (
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  {filteredData.map((item) => (
                    <Card
                      key={item.id}
                      size="small"
                      style={{ borderRadius: 12 }}
                      onClick={() => onOpenDetalhe(item)}
                    >
                      <Space direction="vertical" size={8} style={{ width: "100%" }}>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <Text strong>{item.numeroProtocolo ?? `SR-${new Date().getFullYear()}-${String(item.id).padStart(5, "0")}`}</Text>
                          <SolicitacaoReservaStatusTag
                            status={item.status as StatusSolicitacaoReservaFront}
                          />
                        </div>

                        <div>
                          <Text strong>
                            {`${item.contatoNome ?? ""} ${item.contatoSobrenome ?? ""}`.trim() ||
                              "—"}
                          </Text>
                          <br />
                          <Text type="secondary">{item.contatoCelular || "—"}</Text>
                        </div>

                        <div>
                          <Text type="secondary">Empreendimento: </Text>
                          <Text>{item.empreendimento?.nome ?? "—"}</Text>
                        </div>
                        <div>
                        <Text type="secondary">Check-in: </Text>
                        <Text>
                          {item.periodoProcura?.checkin
                            ? formatCivilDate(item.periodoProcura.checkin)
                            : "—"}
                        </Text>
                      </div>

                      <div>
                        <Text type="secondary">Check-out: </Text>
                        <Text>
                          {item.periodoProcura?.checkout
                            ? formatCivilDate(item.periodoProcura.checkout)
                            : "—"}
                        </Text>
                      </div>

                        <div>
                          <Text type="secondary">Pagamento: </Text>
                          <Text>
                            {item.formaPagamentoPreferida === "CARTAO"
                              ? "Cartão"
                              : item.formaPagamentoPreferida === "PIX"
                              ? "PIX"
                              : "—"}
                          </Text>
                        </div>
                      </Space>
                    </Card>
                  ))}
                </Space>
              )}
            </div>
          ) : (
            <Table
              rowKey="id"
              loading={loading}
              columns={columns}
              dataSource={filteredData}
              pagination={{ pageSize: 10 }}
            />
          )}
        </Card>
      </Space>

      <Drawer
        open={drawerOpen}
        onClose={onCloseDrawer}
        title={selected ? `Solicitação ${selected?.numeroProtocolo ?? `SR-${new Date().getFullYear()}-${String(selected?.id ?? 0).padStart(5, "0")}`}` : "Detalhes"}
        width={700}
        destroyOnClose
      >
        {!selected && !loadingDetalhe ? (
          <Empty description="Nenhum detalhe carregado" />
        ) : (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Card loading={loadingDetalhe} bordered={false} style={{ borderRadius: 12 }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Status">
                  <SolicitacaoReservaStatusTag
                    status={selected?.status as StatusSolicitacaoReservaFront}
                  />
                </Descriptions.Item>

                <Descriptions.Item label="Token">
                  <Text copyable>{selected?.token ?? "—"}</Text>
                </Descriptions.Item>

                <Descriptions.Item label="Contato">
                  {selected
                    ? `${selected.contatoNome ?? ""} ${selected.contatoSobrenome ?? ""}`.trim() ||
                      "—"
                    : "—"}
                </Descriptions.Item>

                <Descriptions.Item label="Celular">
                  {selected?.contatoCelular || "—"}
                </Descriptions.Item>

                <Descriptions.Item label="E-mail">
                  {selected?.contatoEmail || "—"}
                </Descriptions.Item>

                <Descriptions.Item label="Forma de pagamento">
                  {selected?.formaPagamentoPreferida === "CARTAO"
                    ? "Cartão"
                    : selected?.formaPagamentoPreferida === "PIX"
                    ? "PIX"
                    : "—"}
                </Descriptions.Item>

                <Descriptions.Item label="Validade da proposta">
                  {formatDateTime(selected?.validadeProposta)}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="Dados internos" loading={loadingDetalhe} style={{ borderRadius: 12 }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Pessoa ID">
                  {selected?.pessoaId ?? "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Empreendimento ID">
                  {selected?.empreendId ?? "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Origem da procura">
                  {selected?.origemProcura ?? "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Origem da entrada">
                  {selected?.origemEntrada ?? "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Preço base proprietário">
                  {formatMoney(selected?.precoBaseProprietario)}
                </Descriptions.Item>
                <Descriptions.Item label="Valor proposto inicial">
                  {formatMoney(selected?.valorPropostoClienteInicial)}
                </Descriptions.Item>
                <Descriptions.Item label="Valor proposto atual">
                  {formatMoney(selected?.valorPropostoClienteAtual)}
                </Descriptions.Item>
                <Descriptions.Item label="Valor confirmado">
                  {formatMoney(selected?.valorPropostoClienteConfirmado)}
                </Descriptions.Item>
                <Descriptions.Item label="Taxa">
                  {formatMoney(selected?.valorTaxa)}
                </Descriptions.Item>
                <Descriptions.Item label="Empreendimento">
                  {selected?.empreendimento?.nome ?? "—"}
                </Descriptions.Item>

                <Descriptions.Item label="Check-in">
                  {selected?.periodoProcura?.checkin
                    ? formatCivilDate(selected.periodoProcura.checkin)
                    : "—"}
                </Descriptions.Item>

                <Descriptions.Item label="Check-out">
                  {selected?.periodoProcura?.checkout
                    ? formatCivilDate(selected.periodoProcura.checkout)
                    : "—"}
                </Descriptions.Item>

                <Descriptions.Item label="Noites">
                  {selected?.periodoProcura?.noites ?? "—"}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="Termos" loading={loadingDetalhe} style={{ borderRadius: 12 }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Aceitou termos de uso">
                  {selected?.aceitouTermosUso ? "Sim" : "Não"}
                </Descriptions.Item>
                <Descriptions.Item label="Aceitou política de privacidade">
                  {selected?.aceitouPoliticaPrivacidade ? "Sim" : "Não"}
                </Descriptions.Item>
                <Descriptions.Item label="Aceitou termo da taxa">
                  {selected?.aceitouTermoTaxa ? "Sim" : "Não"}
                </Descriptions.Item>
                <Descriptions.Item label="Aceite em">
                  {formatDateTime(selected?.aceiteTermosEm)}
                </Descriptions.Item>
                <Descriptions.Item label="Forma de pagamento definida em">
                  {formatDateTime(selected?.formaPagamentoDefinidaEm)}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="Ações" loading={loadingDetalhe} style={{ borderRadius: 12 }}>
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <Button
                  type="default"
                  onClick={() => onVerHospedes(selected?.token)}
                  block
                >
                  Ver Hóspedes
                </Button>
                <Button
                  type="default"
                  onClick={() => selected && onVerAndamentos(selected.id)}
                  block
                >
                  Ver Andamentos
                </Button>
                {onEstornarTaxa && (
                  <Button
                    danger
                    onClick={onEstornarTaxa}
                    loading={loadingEstornoTaxa}
                    disabled={
                      !selected?.token || selected?.status !== "TAXA_PAGA"
                    }
                    block
                  >
                    Estornar taxa
                  </Button>
                )}
                {onTestarEndpoint && (
                  <Button
                    type="default"
                    onClick={() => selected && onTestarEndpoint(selected.id)}
                    block
                    style={{ borderColor: '#faad14', color: '#faad14' }}
                  >
                    🔍 Testar Endpoint
                  </Button>
                )}
              </Space>
            </Card>

            <Card title="Alterar Status" loading={loadingDetalhe} style={{ borderRadius: 12 }}>
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Select
                  placeholder="Selecione o novo status"
                  value={novoStatus}
                  onChange={onChangeNovoStatus}
                  style={{ width: "100%" }}
                  options={statusOptions}
                />
                <Button
                  type="primary"
                  onClick={() => {
                    if (selected && novoStatus) {
                      onAlterarStatus(selected.id, novoStatus);
                    }
                  }}
                  disabled={!novoStatus || novoStatus === selected?.status}
                  block
                >
                  Alterar Status
                </Button>
              </Space>
            </Card>
          </Space>
        )}
      </Drawer>

      <Drawer
        open={andamentosDrawerOpen}
        onClose={onCloseAndamentosDrawer}
        title="Andamentos da Solicitação"
        width={600}
        destroyOnClose
      >
        {loadingAndamentos ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Space direction="vertical" style={{ width: '100%', alignItems: 'center' }}>
              <div style={{ fontSize: '24px' }}>⏳</div>
              <Text type="secondary">Carregando andamentos...</Text>
            </Space>
          </div>
        ) : andamentos.length === 0 ? (
          <Empty 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <p style={{ marginBottom: '8px', fontWeight: '500' }}>
                  Nenhum andamento registrado
                </p>
                <p style={{ fontSize: '12px', color: '#999' }}>
                  Os andamentos aparecerão aqui conforme a solicitação for processada.
                </p>
              </div>
            } 
            style={{ marginTop: '40px' }}
          />
        ) : (
          <Timeline
            mode="left"
            items={andamentos.map((andamento, index) => {
              console.log(`📋 Renderizando andamento ${index}:`, {
                id: andamento.id,
                created_at: andamento.created_at,
                tipo: andamento.tipo,
                descricao: andamento.descricao,
                usuario: andamento.usuario,
              });

              const getTipoIcon = (tipo: string) => {
                switch (tipo) {
                  case 'SOLICITACAO_CRIADA':
                    return '✨';
                  case 'STATUS_ALTERADO':
                    return '🔄';
                  case 'TAXA_PAGA':
                  case 'PAGAMENTO_REALIZADO':
                    return '💳';
                  case 'RESERVA_CONFIRMADA':
                    return '✅';
                  case 'EMAIL_ENVIADO':
                    return '📧';
                  case 'CONTATO_ATUALIZADO':
                    return '👤';
                  default:
                    return '📝';
                }
              };

              return {
                children: (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ marginRight: '8px', fontSize: '16px' }}>
                        {getTipoIcon(andamento.tipo)}
                      </span>
                      <Text strong style={{ flex: 1 }}>
                        {andamento.descricao}
                      </Text>
                    </div>
                    <div style={{ marginLeft: '24px' }}>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                        {formatDateTime(andamento.created_at)}
                      </Text>
                      {(() => {
                        // Lógica para extrair o nome do autor
                        let autor = '';
                        let icon = '👤';
                        
                        if (andamento.criadoPor) {
                          // Novo formato: criadoPor
                          if (andamento.criadoPor.tipo === 'SISTEMA') {
                            autor = 'Sistema';
                            icon = '⚙️';
                          } else if (andamento.criadoPor.tipo === 'USUARIO' && andamento.criadoPor.nome) {
                            autor = andamento.criadoPor.nome;
                          } else {
                            autor = andamento.criadoPor.tipo || '';
                          }
                        } else if (andamento.usuario) {
                          // Formato antigo: usuario simples
                          autor = andamento.usuario;
                        }
                        
                        if (!autor) return null;
                        
                        return (
                          <Text style={{ fontSize: '12px', color: '#1890ff', fontWeight: '500' }}>
                            {icon} Por: {autor}
                          </Text>
                        );
                      })()}
                    </div>
                  </div>
                ),
                color: andamento.tipo === 'PAGAMENTO_REALIZADO' || andamento.tipo === 'TAXA_PAGA' 
                  ? 'green' 
                  : andamento.tipo === 'RESERVA_CONFIRMADA' 
                  ? 'blue' 
                  : andamento.tipo === 'STATUS_ALTERADO' 
                  ? 'orange' 
                  : 'gray',
              };
            })}
          />
        )}
      </Drawer>

      <ModalHospedesSolicitacao
        open={hospedesModalOpen}
        loading={loadingHospedes}
        hospedes={hospedes}
        protocolo={selected?.numeroProtocolo}
        onClose={onCloseHospedesModal}
      />
    </>
  );
}
